/**
 * Watchdog client — installs global error handlers that POST to the
 * backend watchdog sink so the admin System Health page has a live
 * view of what's crashing in the field.
 *
 * Called once from `index.js` on app boot. Idempotent — safe if the
 * bundle hot-reloads mid-session.
 *
 * Design:
 *  - `window.onerror` catches sync throws in event handlers, image
 *    onerror, script parse errors, etc.
 *  - `window.onunhandledrejection` catches Promise rejections nobody
 *    awaited (the #1 source of silent React failures).
 *  - Axios interceptor annotates 4xx/5xx so we can distinguish
 *    "server crashed" from "user has no internet".
 *  - Client-side rate limiter caps at 20 reports/min so an error loop
 *    can't melt the backend sink. Duplicate-key suppression too:
 *    identical error messages within a 5-second window are collapsed
 *    into one send.
 */
import axios from "axios";
import pkg from "../../package.json";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENDPOINT = `${API}/watchdog/client-error`;

const RATE_LIMIT = 20;          // max sends per window
const RATE_WINDOW_MS = 60_000;  // 1 minute
const DUPE_WINDOW_MS = 5_000;   // collapse identical crashes

const _sendTimes = [];
const _recentSignatures = new Map(); // signature → last-sent ts

let _installed = false;

function _canSend(signature) {
  const now = Date.now();
  // Prune outside window
  while (_sendTimes.length && now - _sendTimes[0] > RATE_WINDOW_MS) {
    _sendTimes.shift();
  }
  if (_sendTimes.length >= RATE_LIMIT) return false;
  const last = _recentSignatures.get(signature);
  if (last && now - last < DUPE_WINDOW_MS) return false;
  _recentSignatures.set(signature, now);
  _sendTimes.push(now);
  return true;
}

function _currentCrewId() {
  try {
    const raw = sessionStorage.getItem("ecDashCrew") || localStorage.getItem("ecDashCrew");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return parsed?.crew_id || parsed?.id || "";
  } catch {
    return "";
  }
}

/**
 * Public API — post one error to the backend sink. Fire-and-forget
 * (never awaits, never throws). External callers (ErrorBoundary) use
 * this too.
 */
export function reportClientError({ message, stack = "", kind = "runtime", componentStack = "", statusCode = null, url = "" }) {
  try {
    const msg = String(message || "").slice(0, 500);
    if (!msg) return;
    // Signature = kind + first 60 chars of message + first stack frame
    const firstFrame = String(stack || "").split("\n").find((l) => l.trim().startsWith("at ")) || "";
    const signature = `${kind}|${msg.slice(0, 60)}|${firstFrame.slice(0, 60)}`;
    if (!_canSend(signature)) return;
    const payload = {
      message: msg,
      stack: String(stack || "").slice(0, 8000),
      component_stack: String(componentStack || "").slice(0, 4000),
      url: (url || window.location?.href || "").slice(0, 500),
      user_agent: (navigator.userAgent || "").slice(0, 400),
      crew_id: _currentCrewId(),
      kind,
      status_code: statusCode,
      build_version: pkg.version || "",
    };
    // Use fetch keepalive so the report survives page navigation.
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(ENDPOINT, blob);
      return;
    }
    fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* swallow — never let telemetry break the app */ });
  } catch {
    /* absolutely nothing */
  }
}

/**
 * Report a SELF-HEAL event — the app caught itself in a dead-end / blank
 * state and auto-recovered (e.g. the blank-screen failsafe forced Home).
 * No JS exception is thrown in these cases, so the normal crash handlers
 * never see them. We surface them to the Watchdog as their own kind so a
 * recurring routing defect can't hide behind the safety net. The backend
 * pushes admins immediately (deduped). Keep `reason`/`role` STABLE per
 * cause (no timestamps) so identical dead-ends collapse to one case.
 * (User 2026-10-01: "nobody was notified ... I found out when I opened it.")
 */
export function reportSelfHeal({ reason = "", role = "", url = "" } = {}) {
  reportClientError({
    message: `Self-heal: recovered blank screen · reason=${reason || "unknown"} · role=${role || "none"}`,
    kind: "self_heal",
    url: url || (typeof window !== "undefined" ? window.location?.href : ""),
  });
}

/**
 * One-time boot. Safe to call multiple times (guarded).
 */
export function installWatchdogClient() {
  if (_installed || typeof window === "undefined") return;
  _installed = true;

  // window.onerror
  const prevOnError = window.onerror;
  window.onerror = function (message, source, lineno, colno, error) {
    reportClientError({
      message: (error && error.message) || String(message || "error"),
      stack: (error && error.stack) || `${source}:${lineno}:${colno}`,
      kind: "runtime",
    });
    if (typeof prevOnError === "function") {
      try { return prevOnError.apply(this, arguments); } catch { /* ignore */ }
    }
    return false;
  };

  // unhandled promise rejections
  window.addEventListener("unhandledrejection", (ev) => {
    const reason = ev?.reason;
    const message = (reason && reason.message) || String(reason || "unhandled rejection");
    const stack = (reason && reason.stack) || "";
    reportClientError({ message, stack, kind: "unhandledrejection" });
  });

  // Axios interceptor — annotates network / server errors. Skips the
  // watchdog endpoint itself so we don't infinite-loop on a broken sink.
  axios.interceptors.response.use(
    (r) => r,
    (error) => {
      try {
        const url = error?.config?.url || "";
        if (url.includes("/watchdog/client-error")) return Promise.reject(error);
        // Ignore aborts/cancellations — those happen every time the user
        // navigates away mid-request or a component unmounts. They're
        // not bugs and reporting them buries real errors under noise.
        // (2026-02-28 — WD-42..WD-51 mass "network offline" incident
        // was actually a Safari tab-switch during page load.)
        const code = error?.code || "";
        const name = error?.name || "";
        if (
          code === "ERR_CANCELED" ||
          code === "ECONNABORTED" ||
          name === "CanceledError" ||
          name === "AbortError" ||
          (axios.isCancel && axios.isCancel(error))
        ) {
          return Promise.reject(error);
        }
        const status = error?.response?.status;
        // Only report 5xx and network failures — 4xx is expected client
        // behaviour (auth check failed, validation, etc.). Filter noisy
        // known-fine paths.
        const isNetwork = !error?.response;
        // Suppress offline blips. When the phone briefly loses network
        // (elevator, dead spot, background→foreground, iOS Safari tab
        // suspend), every in-flight request throws with no response —
        // and the app boot fires 7-10 requests in parallel, so a single
        // 2-second dropout generates a wall of Watchdog events. If the
        // browser tells us we're offline OR if this is a network error
        // AND the boot burst is still in progress (< 5s since load),
        // don't report it. The successful retry once the network
        // returns is signal enough. (User 2026-09-02: WD-592..WD-598,
        // 7 identical "Client offline" events in 2 seconds.)
        if (isNetwork) {
          const offline = typeof navigator !== "undefined" && navigator.onLine === false;
          const bootBurst = typeof performance !== "undefined" && performance.now() < 5000;
          if (offline || bootBurst) return Promise.reject(error);
        }
        if (isNetwork || (status && status >= 500)) {
          reportClientError({
            message: `${status || "NETWORK"} ${error?.config?.method?.toUpperCase() || "GET"} ${url}`,
            stack: error?.stack || "",
            kind: "axios",
            statusCode: status || 0,
            url,
          });
        }
      } catch {
        /* ignore */
      }
      return Promise.reject(error);
    }
  );
}
